package songwritersnotebook.app.components

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Block
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.MoreVert
import androidx.compose.material.icons.filled.Person
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import songwritersnotebook.app.data.Song

import songwritersnotebook.app.billing.SubscriptionTier
import androidx.compose.material.icons.filled.Lock
import androidx.compose.ui.text.style.TextAlign

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ShareSongDialog(
    song: Song,
    onDismiss: () -> Unit,
    onAddCollaborator: (String, Boolean) -> Unit, // email, isEditor
    onRemoveCollaborator: (String) -> Unit, // uid
    onReportCollaborator: (String) -> Unit, // uid
    onBlockCollaborator: (String) -> Unit, // uid
    lookupError: String? = null,
    isLoading: Boolean = false,
    subscriptionTier: SubscriptionTier,
    onUpgrade: () -> Unit
)
{
    var email by remember { mutableStateOf("") }
    var isEditor by remember { mutableStateOf(true) }
    var isAdding by remember { mutableStateOf(false) }
    
    var userToReport by remember { mutableStateOf<Pair<String, String>?>(null) } // uid, email
    var userToBlock by remember { mutableStateOf<Pair<String, String>?>(null) } // uid, email

    Dialog(onDismissRequest = onDismiss) {
        Card(
            shape = RoundedCornerShape(16.dp),
            colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
            modifier = Modifier.fillMaxWidth()
        ) {
            Column(modifier = Modifier.padding(16.dp)) {
                Text(
                    text = "Share \"${song.title}\"",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold
                )
                Spacer(modifier = Modifier.height(16.dp))

                if (subscriptionTier != SubscriptionTier.STUDIO) {
                    // Locked State
                    Column(
                        modifier = Modifier.fillMaxWidth().padding(vertical = 16.dp),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Center
                    ) {
                        Icon(
                            imageVector = Icons.Default.Lock,
                            contentDescription = "Locked",
                            modifier = Modifier.size(48.dp),
                            tint = MaterialTheme.colorScheme.primary
                        )
                        Spacer(modifier = Modifier.height(16.dp))
                        Text(
                            text = "Collaboration is a Studio Feature",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold
                        )
                        Spacer(modifier = Modifier.height(8.dp))
                        Text(
                            text = "Upgrade to Songwriter's Studio to share songs and collaborate in real-time.",
                            style = MaterialTheme.typography.bodyMedium,
                            textAlign = TextAlign.Center,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Spacer(modifier = Modifier.height(24.dp))
                        Button(
                            onClick = {
                                onDismiss()
                                onUpgrade()
                            },
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Text("Upgrade Now")
                        }
                    }
                } else {
                    // Normal Input Section
                    Text("Add People", style = MaterialTheme.typography.labelMedium)
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        OutlinedTextField(
                            value = email,
                            onValueChange = { email = it; isAdding = false }, // Reset error on type
                            label = { Text("Email or Username") },
                            modifier = Modifier.weight(1f),
                            singleLine = true,
                            isError = lookupError != null && !isAdding,
                            enabled = !isLoading
                        )
                    }
                    
                    Spacer(modifier = Modifier.height(8.dp))
                    
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        // Role Toggle
                        Row(
                            modifier = Modifier
                                .height(32.dp)
                                .padding(end = 8.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            FilterChip(
                                selected = isEditor,
                                onClick = { isEditor = true },
                                label = { Text("Can Edit") },
                                modifier = Modifier.padding(end = 4.dp),
                                enabled = !isLoading
                            )
                            FilterChip(
                                selected = !isEditor,
                                onClick = { isEditor = false },
                                label = { Text("View Only") },
                                enabled = !isLoading
                            )
                        }

                        if (isLoading) {
                            CircularProgressIndicator(modifier = Modifier.size(24.dp), strokeWidth = 2.dp)
                        } else {
                            FilledIconButton(
                                onClick = {
                                    if (email.isNotBlank()) {
                                        isAdding = true
                                        onAddCollaborator(email, isEditor)
                                    }
                                },
                                modifier = Modifier.size(36.dp),
                                enabled = email.isNotBlank()
                            ) {
                                if (lookupError != null && isAdding) {
                                    // Show error state? Just icon.
                                    Icon(Icons.Default.Add, "Try Again") 
                                } else {
                                    Icon(Icons.Default.Add, "Add")
                                }
                            }
                        }
                    }
                    
                    if (lookupError != null) {
                        Text(
                            text = lookupError,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodySmall,
                            modifier = Modifier.padding(top = 4.dp)
                        )
                    }

                    HorizontalDivider(modifier = Modifier.padding(vertical = 16.dp))

                    // List Section
                    Text("Access List", style = MaterialTheme.typography.labelMedium)
                    LazyColumn(
                        modifier = Modifier
                            .heightIn(max = 200.dp)
                            .fillMaxWidth()
                    ) {
                        // Owner
                        item {
                            UserRow(
                                email = "Owner", // We don't have owner email easily, just label it
                                role = "Owner",
                                isRemovable = false,
                                onRemove = {},
                                onReport = {},
                                onBlock = {},
                                isLoading = isLoading
                            )
                        }
                        
                        // Collaborators
                        items(song.collaborators) { uid ->
                            val userEmail = song.collaboratorEmails[uid] ?: "Unknown User"
                            UserRow(
                                email = userEmail,
                                role = "Can Edit",
                                isRemovable = true,
                                onRemove = { onRemoveCollaborator(uid) },
                                onReport = { userToReport = uid to userEmail },
                                onBlock = { userToBlock = uid to userEmail },
                                isLoading = isLoading
                            )
                        }
                        items(song.viewers) { uid ->
                            val userEmail = song.collaboratorEmails[uid] ?: "Unknown User"
                            UserRow(
                                email = userEmail,
                                role = "View Only",
                                isRemovable = true,
                                onRemove = { onRemoveCollaborator(uid) },
                                onReport = { userToReport = uid to userEmail },
                                onBlock = { userToBlock = uid to userEmail },
                                isLoading = isLoading
                            )
                        }
                        
                        // Pending Invites
                        song.pendingCollaborators.forEach { (email, role) ->
                            item {
                                UserRow(
                                    email = email,
                                    role = "Pending (${role.replaceFirstChar { it.uppercase() }})",
                                    isRemovable = true,
                                    onRemove = { onRemoveCollaborator(email) },
                                    onReport = {},
                                    onBlock = {},
                                    isLoading = isLoading
                                )
                            }
                        }
                    }
                } // End else

                Spacer(modifier = Modifier.height(16.dp))
                Button(
                    onClick = onDismiss,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(if (subscriptionTier != SubscriptionTier.STUDIO) "Cancel" else "Done")
                }
            }

            // Confirmation Dialogs (Inside Card for visibility)
            if (userToReport != null) {
                AlertDialog(
                    onDismissRequest = { userToReport = null },
                    icon = { Icon(Icons.Default.Warning, null) },
                    title = { Text("Report User?") },
                    text = { Text("This will prepare a report for our moderation team. You will be redirected to your email app to provide more details about this user's behavior.") },
                    confirmButton = {
                        TextButton(onClick = { 
                            onReportCollaborator(userToReport!!.first)
                            userToReport = null 
                        }) {
                            Text("Report")
                        }
                    },
                    dismissButton = {
                        TextButton(onClick = { userToReport = null }) {
                            Text("Cancel")
                        }
                    }
                )
            }

            if (userToBlock != null) {
                AlertDialog(
                    onDismissRequest = { userToBlock = null },
                    icon = { Icon(Icons.Default.Block, null, tint = MaterialTheme.colorScheme.error) },
                    title = { Text("Block User?") },
                    text = { Text("Blocking ${userToBlock!!.second} will remove them from all your songs and prevent them from sending you future invites. This action can be undone in Settings.") },
                    confirmButton = {
                        TextButton(onClick = { 
                            onBlockCollaborator(userToBlock!!.first)
                            userToBlock = null 
                        }) {
                            Text("Block", color = MaterialTheme.colorScheme.error)
                        }
                    },
                    dismissButton = {
                        TextButton(onClick = { userToBlock = null }) {
                            Text("Cancel")
                        }
                    }
                )
            }
        }
    }
}

@Composable
fun UserRow(
    email: String,
    role: String,
    isRemovable: Boolean,
    onRemove: () -> Unit,
    onReport: () -> Unit,
    onBlock: () -> Unit,
    isLoading: Boolean = false
)
{
    var showMenu by remember { mutableStateOf(false) }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            imageVector = Icons.Default.Person,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier
                .size(32.dp)
                .padding(end = 12.dp)
        )
        Column(modifier = Modifier.weight(1f)) {
            Text(text = email, style = MaterialTheme.typography.bodyMedium)
            Text(
                text = role,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
        
        if (isRemovable) {
            Box {
                if (isLoading) {
                    CircularProgressIndicator(modifier = Modifier.size(24.dp).padding(12.dp), strokeWidth = 2.dp)
                } else {
                    IconButton(onClick = { showMenu = true }) {
                        Icon(
                            imageVector = Icons.Default.MoreVert,
                            contentDescription = "Options",
                            tint = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                }
                DropdownMenu(
                    expanded = showMenu,
                    onDismissRequest = { showMenu = false }
                ) {
                    DropdownMenuItem(
                        text = { Text("Remove") },
                        onClick = {
                            showMenu = false
                            onRemove()
                        },
                        leadingIcon = { Icon(Icons.Default.Close, null, tint = MaterialTheme.colorScheme.error) }
                    )
                    DropdownMenuItem(
                        text = { Text("Report User") },
                        onClick = {
                            showMenu = false
                            onReport()
                        },
                        leadingIcon = { Icon(Icons.Default.Warning, null) }
                    )
                    DropdownMenuItem(
                        text = { Text("Block User") },
                        onClick = {
                            showMenu = false
                            onBlock()
                        },
                        leadingIcon = { Icon(Icons.Default.Block, null, tint = MaterialTheme.colorScheme.error) }
                    )
                }
            }
        }
    }
}
